import React from 'react';
import { FaqItem, FictionalIcon } from '../../customFrontend/parts';
import { ChatappCity } from '../../siteBrands/chatapp/types';

/**
 * The city pages' copy, standard German with informal "du", written for
 * Germany (not translated from chatapp.nl). Each city is written for that city
 * (never a template with the name swapped: search engines treat those as
 * doorway pages). Local flavour comes from places and facts, not from dialect.
 */
export type CityContent = ChatappCity;

const disclosure = (city: string): FaqItem => ({
  q: `Sind die Profile in ${city} echte Menschen, die ich treffen kann?`,
  a: (
    <p>
      Nein. Chatapp nutzt fiktive Profile, deren Ziel es ist, mit Nutzern zu flirten. Du erkennst sie am
      Personensymbol <FictionalIcon language="de" /> in ihrem Profil, und physischer Kontakt mit diesen Profilen ist
      nicht möglich. Chatapp ist ein Chat zur Unterhaltung, keine Plattform für Verabredungen.
    </p>
  ),
});

export const CITIES: Record<string, CityContent> = {
  '/berlin': {
    path: '/berlin',
    slug: 'berlin',
    name: 'Berlin',
    province: 'Stadtstaat',
    kicker: 'Hauptstadt an der Spree',
    h1: 'Sexchat Berlin: Flirtchat für eine Stadt, die nie schläft',
    lead: 'Kostenlos anmelden, die ersten Nachrichten gratis und ein Chat, der so lange offen hat wie die Clubs.',
    intro: (
      <p>
        Berlin hat fast vier Millionen Einwohner, zwölf Bezirke und gefühlt für jede Laune einen eigenen Kiez. Hier
        fragt niemand, wann du nach Hause gehst, und genau so locker darf auch ein Flirt sein. Bei Chatapp legst du
        in einer Minute ein Profil an, schreibst deine ersten Nachrichten gratis und entscheidest selbst, wie weit
        das Gespräch geht.
      </p>
    ),
    sections: [
      {
        title: 'Von Kreuzberg bis Prenzlauer Berg',
        body: (
          <>
            <p>
              Ein Berliner Abend kann an der Admiralbrücke beginnen, wenn sich dort im Sommer die Leute mit einem Getränk vom Kiosk auf das Geländer setzen. In Neukölln reihen sich auf der Weserstraße die Bars
              aneinander, in Friedrichshain füllt sich die Simon-Dach-Straße, und am Kurfürstendamm geht es etwas
              eleganter zu. Wer am Wochenende in einen Club will, rechnet in Berlin nicht in Stunden, sondern in
              Nächten.
            </p>
            <p>
              Dazwischen liegen lange Wege. Die Ringbahn braucht für eine Runde rund eine Stunde, und viele nutzen
              die Zeit in der S41 oder S42 für ein paar Nachrichten, die mit jeder Station etwas mutiger werden.
            </p>
          </>
        ),
      },
      {
        title: 'Anonym in der großen Stadt',
        body: (
          <p>
            In Berlin kann man jeden Tag neu sein, und auf Chatapp ebenso. Du chattest unter einem Nickname, dein
            Name und deine E-Mail-Adresse bleiben unsichtbar, und in deinen sozialen Netzwerken erscheint nichts.
            Ob du in Mitte im Büro sitzt oder in Lichtenberg auf dem Balkon: Was du schreibst, bleibt zwischen dir
            und deinem Bildschirm.
          </p>
        ),
      },
      {
        title: 'Wann Berlin chattet',
        body: (
          <p>
            Unter der Woche wird es nach Feierabend lebhaft, wenn die U-Bahnen voll sind und alle auf dem Heimweg
            sind. Am Sonntag sitzt halb Berlin auf dem Tempelhofer Feld oder im Mauerpark, das Handy griffbereit.
            Und nach einer langen Clubnacht ist ein Flirt im Bett manchmal genau das Richtige. Chatapp läuft im
            Browser, du brauchst also keine App und machst einfach da weiter, wo du aufgehört hast.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Was kostet der Sexchat in Berlin bei Chatapp?',
        a: <p>Die Anmeldung ist kostenlos, und deine ersten Nachrichten sind gratis. Danach kosten Nachrichten, Flirts und Fotos Credits. Credits kaufst du nur, wenn du weiterschreiben möchtest, und jeden Preis siehst du vor dem Bezahlen.</p>,
      },
      {
        q: 'Muss ich bei der Anmeldung eine Kreditkarte angeben?',
        a: <p>Nein. Bei der Anmeldung fragen wir weder nach einer Kreditkarte noch nach anderen Zahlungsdaten. Du wählst einen Nickname und kannst sofort anfangen.</p>,
      },
      disclosure('Berlin'),
    ],
    cta: {
      title: 'Die erste Nachricht ist gratis',
      text: 'Ein Profil ist in einer Minute angelegt. Ob Kreuzberg oder Köpenick: Dein erster Flirt kann heute Abend starten.',
      button: 'Kostenlos anmelden',
    },
  },

  '/hamburg': {
    path: '/hamburg',
    slug: 'hamburg',
    name: 'Hamburg',
    province: 'Stadtstaat',
    kicker: 'Hafenstadt an der Elbe',
    h1: 'Sexchat Hamburg: Flirtchat zwischen Elbe und Alster',
    lead: 'Ein kostenloses Profil, gratis erste Nachrichten und Gespräche, die bei jedem Wetter warm bleiben.',
    intro: (
      <p>
        Hamburg ist mit rund 1,9 Millionen Einwohnern die zweitgrößte Stadt Deutschlands und hat mehr Brücken als
        Venedig. Die Stadt wirkt auf den ersten Blick zurückhaltend, doch hinter der hanseatischen Fassade wird
        gern gefeiert. Auf Chatapp darfst du beides sein, kühl im Ton und frech in der Sache. Die Anmeldung kostet
        nichts, und die ersten Nachrichten auch nicht.
      </p>
    ),
    sections: [
      {
        title: 'Von der Schanze bis zum Fischmarkt',
        body: (
          <>
            <p>
              Viele Hamburger Abende beginnen im Schanzenviertel, wo die Tische vor den Bars bis spät besetzt sind.
              Von dort ist es nicht weit nach St. Pauli und auf die Reeperbahn, wo die Clubs erst nach Mitternacht
              richtig voll werden. In Ottensen geht es ruhiger zu, mit kleinen Restaurants rund um den Spritzenplatz.
              Wer die Nacht wirklich durchmacht, landet am Sonntagmorgen auf dem Fischmarkt an der Elbe.
            </p>
            <p>
              Tagsüber fährt die Hafenfähre 62 von den Landungsbrücken nach Finkenwerder, für viele die schönste
              Strecke der Stadt, und ein guter Moment, um zwischen zwei Anlegern eine Nachricht zu schicken.
            </p>
          </>
        ),
      },
      {
        title: 'Hanseatisch diskret',
        body: (
          <p>
            In Hamburg redet man nicht gern über Privates, und das musst du auf Chatapp auch nicht. Du schreibst
            unter einem Nickname, niemand sieht deinen Namen oder deine E-Mail-Adresse, und welche Benachrichtigungen
            per E-Mail kommen, legst du selbst fest. Ob in der HafenCity mit Blick auf die Elbphilharmonie oder in
            Barmbek auf dem Sofa: Dein Flirt gehört dir.
          </p>
        ),
      },
      {
        title: 'Wenn es draußen grau wird',
        body: (
          <p>
            Hamburg ist für seinen Regen bekannt, und an solchen Tagen ist das Handy oft der gemütlichste Ort der
            Stadt. Im Sommer sitzen die Leute an der Außenalster oder am Elbstrand in Övelgönne und schreiben
            zwischendurch. Im Winter zieht sich die Stadt früh nach drinnen zurück, und die Gespräche werden länger.
            Chatapp läuft im Browser, ganz ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Wie viele Nachrichten sind in Hamburg gratis?',
        a: <p>Deine ersten Nachrichten sind gratis, damit du in Ruhe ausprobieren kannst, ob dir der Chat gefällt. Danach kosten Nachrichten, Flirts und Fotos Credits. Die Preise stehen auf der Seite, bevor du etwas kaufst.</p>,
      },
      {
        q: 'Brauche ich eine App für Chatapp?',
        a: <p>Nein. Chatapp funktioniert im Browser deines Handys, Tablets oder Computers. Du öffnest die Seite, meldest dich an und chattest los.</p>,
      },
      disclosure('Hamburg'),
    ],
    cta: {
      title: 'Starte deinen ersten Chat',
      text: 'Kostenlos anmelden dauert eine Minute, und die ersten Nachrichten gehen nicht auf deine Rechnung. Von Altona bis Wandsbek.',
      button: 'Gratis Profil anlegen',
    },
  },

  '/muenchen': {
    path: '/muenchen',
    slug: 'muenchen',
    name: 'München',
    province: 'Bayern',
    kicker: 'Landeshauptstadt an der Isar',
    h1: 'Sexchat München: Flirtchat zwischen Biergarten und Isar',
    lead: 'Kostenlos anmelden, die ersten Nachrichten gratis und ein Flirt, der so entspannt beginnt wie ein Abend am Fluss.',
    intro: (
      <p>
        München hat rund 1,5 Millionen Einwohner und den Ruf, die schönste Großstadt am Rand der Alpen zu sein. Hier
        trifft Tradition auf Start-ups, Lederhose auf Laptop. Wer abends die Krawatte ablegt, hat oft Lust auf etwas
        Leichtigkeit. Bei Chatapp legst du gratis ein Profil an, schreibst deine ersten Nachrichten ohne zu bezahlen
        und bestimmst selbst, wie prickelnd es wird.
      </p>
    ),
    sections: [
      {
        title: 'Vom Glockenbachviertel nach Schwabing',
        body: (
          <>
            <p>
              Im Glockenbachviertel und rund um den Gärtnerplatz sitzen an warmen Abenden die Leute bis spät auf den
              Stufen und vor den Bars. In der Maxvorstadt prägen die Studierenden der Universitäten das Bild, in
              Schwabing die Cafés auf der Leopoldstraße. Im Sommer zieht es halb München an die Isar, zum Grillen an
              den Kiesbänken oder zum Zuschauen bei den Surfern am Eisbach im Englischen Garten.
            </p>
            <p>
              Und dann sind da die Biergärten, vom Chinesischen Turm bis zum Augustiner-Keller. Unter Kastanien und
              mit einer Maß auf dem Tisch ist eine kurze Nachricht schnell geschrieben.
            </p>
          </>
        ),
      },
      {
        title: 'Diskret, auch in einer kleinen Großstadt',
        body: (
          <p>
            München ist groß, fühlt sich aber manchmal an wie ein Dorf: Irgendwer kennt immer irgendwen. Auf Chatapp
            bist du nur ein Nickname. Dein Name und deine E-Mail-Adresse bleiben verborgen, und in deinen sozialen
            Netzwerken taucht nichts auf. So bleibt dein Flirt privat, egal ob du in Bogenhausen wohnst oder in
            Giesing.
          </p>
        ),
      },
      {
        title: 'Wann München chattet',
        body: (
          <p>
            Wenn im September das Oktoberfest auf der Theresienwiese beginnt, ist die Stadt zwei Wochen lang im
            Ausnahmezustand, und die Nachrichten danach werden länger. Im Winter fahren viele am Wochenende in die
            Berge und schreiben auf der Rückfahrt in der S-Bahn. Unter der Woche wird es nach Feierabend lebhaft. Da
            Chatapp im Browser läuft, brauchst du keine App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Was kostet Chatapp in München?',
        a: <p>Ein Profil anzulegen ist kostenlos, ebenso deine ersten Nachrichten. Danach kosten Nachrichten, Flirts und Fotos Credits. Du kaufst Credits nur, wenn du weiterchatten willst, und siehst jeden Preis vorher.</p>,
      },
      {
        q: 'Sieht jemand, dass ich angemeldet bin?',
        a: <p>Andere Nutzer sehen nur deinen Nickname. Dein Name, deine E-Mail-Adresse und deine Kontakte bleiben privat, und auf sozialen Netzwerken wird nichts veröffentlicht.</p>,
      },
      disclosure('München'),
    ],
    cta: {
      title: 'Ein Flirt zum Feierabend',
      text: 'Melde dich in einer Minute kostenlos an und schick deine ersten Nachrichten gratis, zwischen Isar und Olympiapark.',
      button: 'Kostenlos anmelden',
    },
  },

  '/koeln': {
    path: '/koeln',
    slug: 'koeln',
    name: 'Köln',
    province: 'Nordrhein-Westfalen',
    kicker: 'Domstadt am Rhein',
    h1: 'Sexchat Köln: Flirtchat in der Stadt am Rhein',
    lead: 'Gratis Profil, kostenlose erste Nachrichten und ein Chat für eine Stadt, die gern feiert.',
    intro: (
      <p>
        Köln zählt gut eine Million Einwohner und gilt als eine der offensten Städte des Landes. Wer hier neu ist,
        steht oft schon am ersten Abend mit Fremden an der Theke. Diese Offenheit passt zu Chatapp: Du meldest dich
        kostenlos an, schreibst die ersten Nachrichten gratis und probierst aus, wie viel Flirt in einem Gespräch
        steckt.
      </p>
    ),
    sections: [
      {
        title: 'Vom Belgischen Viertel nach Ehrenfeld',
        body: (
          <>
            <p>
              Im Belgischen Viertel treffen sich die Leute in kleinen Bars und auf dem Brüsseler Platz, wo an
              Sommerabenden hunderte Menschen vor der Kirche sitzen. Rund um die Zülpicher Straße feiern die
              Studierenden, in Ehrenfeld finden Konzerte und Partys in alten Fabrikhallen statt. In der Südstadt geht
              es gemütlicher zu, und am Rheinauhafen mit seinen Kranhäusern trinkt man ein Glas mit Blick aufs
              Wasser.
            </p>
            <p>
              Das Kölsch kommt in schmalen Stangen und wird so lange nachgeschenkt, bis du deinen Deckel auf das Glas
              legst. Ein Gespräch auf Chatapp funktioniert ähnlich: Du entscheidest, wann es genug ist.
            </p>
          </>
        ),
      },
      {
        title: 'Zwischen Karneval und Alltag',
        body: (
          <p>
            Ab dem 11.11. und besonders an Weiberfastnacht und Rosenmontag steht die Stadt kopf. In diesen Tagen
            schlüpfen viele Kölnerinnen und Kölner in eine andere Rolle, und auch im Chat darf man jemand anderes
            sein. Den Rest des Jahres arbeitet Köln in Medienhäusern, Versicherungen und am Messegelände in Deutz.
            Ein Nickname genügt, dein Name und deine E-Mail-Adresse bleiben geheim.
          </p>
        ),
      },
      {
        title: 'Wann Köln chattet',
        body: (
          <p>
            Morgens schicken Pendler auf der Hohenzollernbrücke, wo tausende Liebesschlösser am Geländer hängen,
            aus dem Zug eine schnelle Nachricht. Abends wird es nach dem Kneipenbesuch auf dem Heimweg lebhaft, und
            am Wochenende vertrödeln viele den Nachmittag auf den Poller Wiesen. Chatapp läuft im Browser, ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Ist der Sexchat in Köln kostenlos?',
        a: <p>Die Anmeldung und deine ersten Nachrichten sind kostenlos. Danach brauchst du Credits für Nachrichten, Flirts und Fotos. Du kaufst nur dann ein Paket, wenn du weiterschreiben möchtest, und siehst den Preis vorher.</p>,
      },
      {
        q: 'Kann ich auch mit Männern chatten?',
        a: <p>Ja. Bei der Anmeldung gibst du an, mit wem du chatten möchtest, mit Frauen oder mit Männern.</p>,
      },
      disclosure('Köln'),
    ],
    cta: {
      title: 'Ein Flirt, wann du willst',
      text: 'Kostenlos anmelden und die ersten Nachrichten gratis schreiben, ob in Nippes, Sülz oder Deutz.',
      button: 'Jetzt gratis starten',
    },
  },

  '/frankfurt': {
    path: '/frankfurt',
    slug: 'frankfurt',
    name: 'Frankfurt am Main',
    province: 'Hessen',
    kicker: 'Bankenstadt am Main',
    h1: 'Sexchat Frankfurt am Main: Flirtchat unter der Skyline',
    lead: 'Kostenlos anmelden, die ersten Nachrichten gratis und ein Flirt, der in jede Reisepause passt.',
    intro: (
      <p>
        Frankfurt am Main hat rund 770.000 Einwohner, doch an Werktagen kommen Hunderttausende Pendler dazu. Hier
        stehen die meisten Hochhäuser Deutschlands, die Europäische Zentralbank und der größte Flughafen des Landes.
        Wer so viel unterwegs ist, schätzt einen Chat, der überall funktioniert. Bei Chatapp meldest du dich
        kostenlos an und schreibst deine ersten Nachrichten gratis.
      </p>
    ),
    sections: [
      {
        title: 'Von Sachsenhausen bis zur Berger Straße',
        body: (
          <>
            <p>
              Südlich des Mains, in Alt-Sachsenhausen, sitzen die Gäste in traditionellen Apfelweinwirtschaften an
              langen Holztischen. Am Museumsufer reihen sich die Häuser für Kunst und Film, und über den Eisernen
              Steg spaziert man zurück in die Innenstadt. Im Nordend und in Bornheim ist die Berger Straße die
              Lebensader, mit Cafés und Weinbars, die bis spät geöffnet haben.
            </p>
            <p>
              Im Bahnhofsviertel liegen Imbisse, Bars und neue Restaurants dicht beieinander. Viele, die hier nur auf
              der Durchreise sind, vertreiben sich die Zeit bis zum nächsten Zug mit ein paar Zeilen auf dem Handy.
            </p>
          </>
        ),
      },
      {
        title: 'Diskret, auch auf Dienstreise',
        body: (
          <p>
            Frankfurt ist eine Stadt der Messen, Konferenzen und Hotelzimmer. Wenn der Tag mit Terminen gefüllt war,
            ist ein Gespräch am Abend eine willkommene Abwechslung. Auf Chatapp bist du nur ein Nickname: Niemand
            sieht deinen Namen oder deine E-Mail-Adresse, und in deinen beruflichen Netzwerken taucht nichts auf.
          </p>
        ),
      },
      {
        title: 'Wann Frankfurt chattet',
        body: (
          <p>
            Morgens und abends sind S-Bahnen und Regionalzüge voller Pendler aus dem Taunus und der Wetterau. Im
            Sommer liegen die Leute am Mainufer, und von der Plattform des Main Towers sieht man die Lichter der
            Stadt. Nachts, wenn die Bürotürme leer sind, wird der Chat ruhiger und persönlicher. Und wer am Flughafen auf einen verspäteten Anschlussflug wartet, merkt schnell, wie viel kürzer sich eine Stunde am Gate mit einem guten Gespräch anfühlt. Chatapp läuft im
            Browser, ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Was kostet Chatapp in Frankfurt?',
        a: <p>Die Anmeldung ist gratis, und die ersten Nachrichten auch. Danach kosten Nachrichten, Flirts und Fotos Credits. Jeden Preis siehst du, bevor du bezahlst.</p>,
      },
      {
        q: 'Kann ich Chatapp auch unterwegs nutzen?',
        a: <p>Ja. Chatapp läuft im Browser deines Handys, ohne App. Du kannst im Zug, am Flughafen oder im Hotel weiterschreiben, wo du aufgehört hast.</p>,
      },
      disclosure('Frankfurt'),
    ],
    cta: {
      title: 'Deine ersten Nachrichten sind gratis',
      text: 'Ein Profil ist in einer Minute angelegt, ob im Nordend, in Bockenheim oder in Höchst.',
      button: 'Kostenlos anmelden',
    },
  },

  '/stuttgart': {
    path: '/stuttgart',
    slug: 'stuttgart',
    name: 'Stuttgart',
    province: 'Baden-Württemberg',
    kicker: 'Landeshauptstadt im Talkessel',
    h1: 'Sexchat Stuttgart: Flirtchat zwischen Weinbergen und Schlossplatz',
    lead: 'Ein Profil ohne Kosten, gratis erste Nachrichten und ein Chat, der nach Schichtende genauso offen ist wie spät am Abend.',
    intro: (
      <p>
        Stuttgart liegt in einem Talkessel, umgeben von Hügeln, Wäldern und Weinbergen, die bis an die Wohnviertel
        reichen. Die Stadt hat rund 630.000 Einwohner und ist für ihre Autoindustrie und ihren Fleiß bekannt. Umso
        schöner ist es, abends abzuschalten. Bei Chatapp meldest du dich kostenlos an, schreibst die ersten
        Nachrichten gratis und lässt den Tag mit einem Flirt ausklingen.
      </p>
    ),
    sections: [
      {
        title: 'Vom Schlossplatz zur Theodor-Heuss-Straße',
        body: (
          <>
            <p>
              Im Sommer ist der Schlossplatz das Wohnzimmer der Stadt, die Wiesen voll mit Leuten zwischen Neuem
              Schloss und Königsbau. Abends geht es weiter in die Theodor-Heuss-Straße, wo sich Bars und Clubs
              aneinanderreihen, oder ins Hans-im-Glück-Viertel mit seinen kleinen Kneipen. Im Bohnenviertel ist es
              ruhiger, mit Weinstuben in alten Handwerkerhäusern.
            </p>
            <p>
              Von vielen Stellen der Stadt führen Treppen hinauf an den Hang. Wer oben ankommt, hat den Kessel unter
              sich und das Handy in der Hand.
            </p>
          </>
        ),
      },
      {
        title: 'Nach der Schicht',
        body: (
          <p>
            Zwischen Untertürkheim, Zuffenhausen und den Zulieferern im Umland arbeiten viele im Schichtbetrieb. Das
            merkt man im Chat: Auch am frühen Nachmittag und spät in der Nacht ist jemand unterwegs. Ob du von der
            Frühschicht kommst oder nach der Spätschicht nicht schlafen kannst, Chatapp läuft im Browser, und ein
            Nickname reicht. Name und E-Mail-Adresse bleiben privat.
          </p>
        ),
      },
      {
        title: 'Wann Stuttgart chattet',
        body: (
          <p>
            Im Herbst locken die Besenwirtschaften und das Volksfest auf dem Cannstatter Wasen, im Sommer die
            Weinwanderwege am Rotenberg und der Blick vom Fernsehturm. Unter der Woche wird es nach Feierabend lebhaft, wenn die
            Stadtbahnen aus dem Zentrum fahren. Und an Regentagen, wenn der Kessel unter Wolken liegt, sind die
            Gespräche oft am längsten. Und wenn im Dezember der Weihnachtsmarkt rund um das Alte Schloss und den Marktplatz öffnet, wärmt man sich danach gern mit einem Gespräch auf dem Sofa auf.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Was kostet der Flirtchat in Stuttgart?',
        a: <p>Anmelden kostet nichts, und deine ersten Nachrichten sind gratis. Danach zahlst du mit Credits für Nachrichten, Flirts und Fotos. Credits kaufst du nur bei Bedarf, und der Preis steht vorher fest.</p>,
      },
      {
        q: 'Bekomme ich ungefragt E-Mails?',
        a: <p>Du entscheidest in deinen Einstellungen selbst, welche Benachrichtigungen du per E-Mail bekommst. Du kannst sie jederzeit ändern oder ganz abstellen.</p>,
      },
      disclosure('Stuttgart'),
    ],
    cta: {
      title: 'Heute Abend abschalten',
      text: 'Melde dich in einer Minute kostenlos an, von Bad Cannstatt bis Vaihingen, und schick deine ersten Nachrichten gratis.',
      button: 'Gratis Profil anlegen',
    },
  },

  '/duesseldorf': {
    path: '/duesseldorf',
    slug: 'duesseldorf',
    name: 'Düsseldorf',
    province: 'Nordrhein-Westfalen',
    kicker: 'Landeshauptstadt am Rhein',
    h1: 'Sexchat Düsseldorf: Flirtchat mit Stil',
    lead: 'Kostenlos anmelden, die ersten Nachrichten gratis und ein Flirt, der so elegant sein darf wie die Kö.',
    intro: (
      <p>
        Düsseldorf ist die Landeshauptstadt von Nordrhein-Westfalen, eine Stadt der Mode, der Werbung und der Kunst,
        mit gut 620.000 Einwohnern. Man zeigt sich gern, auf der Königsallee genauso wie an der Rheinuferpromenade.
        Auf Chatapp darfst du dich zeigen, wie du willst: unter einem Nickname, mit gratis ersten Nachrichten und in
        deinem eigenen Tempo.
      </p>
    ),
    sections: [
      {
        title: 'Von der Altstadt bis zum Medienhafen',
        body: (
          <>
            <p>
              Die Altstadt nennt sich gern die längste Theke der Welt: Auf weniger als einem Quadratkilometer liegen
              Hunderte Kneipen, Bars und Brauhäuser, in denen das Altbier frisch gezapft wird. Ein paar Schritte
              weiter flaniert man auf der Rheinuferpromenade, und im Medienhafen stehen die geschwungenen Bauten von
              Frank Gehry neben Restaurants mit Blick aufs Wasser.
            </p>
            <p>
              In Flingern und Unterbilk findest du Cafés, Galerien und kleine Läden, rund um die Immermannstraße die
              größte japanische Gemeinde Deutschlands mit Ramen-Bars, vor denen abends Schlangen stehen.
            </p>
          </>
        ),
      },
      {
        title: 'Ein Nickname statt Visitenkarte',
        body: (
          <p>
            In einer Stadt voller Agenturen, Kanzleien und Konzernzentralen kennt man sich schnell. Auf Chatapp musst
            du dich nicht vorstellen: Du chattest unter einem Nickname, Name und E-Mail-Adresse bleiben unsichtbar,
            und in deinen Netzwerken erscheint nichts. So bleibt die Kö deine Bühne und der Chat deine Privatsache.
          </p>
        ),
      },
      {
        title: 'Wann Düsseldorf chattet',
        body: (
          <p>
            Zur Karnevalszeit und während großer Messen wie der Boot oder der Medica ist die Stadt voll, und abends
            in den Hotels wird viel geschrieben. Im Sommer sitzen die Leute auf den Rheinwiesen in Oberkassel und
            sehen zu, wie auf der anderen Seite die Lichter der Altstadt angehen. Unter der Woche wird es nach Feierabend lebhaft, wenn die Rheinbahn aus der Innenstadt Richtung Benrath, Gerresheim und Kaiserswerth fährt und viele die Fahrt für ein paar Zeilen nutzen. Chatapp läuft im Browser, ganz ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Was kostet Chatapp in Düsseldorf?',
        a: <p>Die Anmeldung und deine ersten Nachrichten kosten nichts. Danach kosten Nachrichten, Flirts und Fotos Credits. Ein Paket kaufst du nur, wenn du weiterchatten möchtest, und jeden Preis siehst du vorab.</p>,
      },
      {
        q: 'Wie lege ich ein Profil an?',
        a: <p>Du gibst an, mit wem du chatten möchtest, und wählst einen Nickname. Nach einer Minute kannst du die ersten Nachrichten schreiben.</p>,
      },
      disclosure('Düsseldorf'),
    ],
    cta: {
      title: 'Zeig dich von deiner frechen Seite',
      text: 'Kostenlos anmelden, ob in Pempelfort oder Oberkassel, und die ersten Nachrichten gratis verschicken.',
      button: 'Kostenlos anmelden',
    },
  },

  '/leipzig': {
    path: '/leipzig',
    slug: 'leipzig',
    name: 'Leipzig',
    province: 'Sachsen',
    kicker: 'Messestadt in Sachsen',
    h1: 'Sexchat Leipzig: Flirtchat von der Karli bis zum See',
    lead: 'Gratis anmelden, die ersten Nachrichten kostenlos und ein Chat für eine Stadt, die ständig in Bewegung ist.',
    intro: (
      <p>
        Leipzig ist die größte Stadt Sachsens und eine der am schnellsten wachsenden Städte Deutschlands, mit mehr
        als 600.000 Einwohnern. Hier begannen 1989 die Montagsdemonstrationen, hier spielte Bach in der
        Thomaskirche, und heute ziehen günstige Altbauwohnungen und eine lebendige Kulturszene junge Leute an. Bei
        Chatapp meldest du dich kostenlos an und probierst gratis aus, wie dir der Chat gefällt.
      </p>
    ),
    sections: [
      {
        title: 'Von der Karli nach Plagwitz',
        body: (
          <>
            <p>
              Die Karl-Liebknecht-Straße, von allen nur Karli genannt, ist mit ihren Bars, Kiosken und Restaurants
              die Ausgehmeile der Südvorstadt. In Connewitz geht es alternativer zu, in Plagwitz und Lindenau
              liegen Ateliers, Cafés und Clubs in alten Fabriken an den Kanälen. In der Baumwollspinnerei, einst die
              größte Spinnerei Europas, sind heute Galerien und Künstlerwerkstätten zu Hause.
            </p>
            <p>
              In der Innenstadt erinnert Auerbachs Keller an Goethes Faust, und die überdachte Mädlerpassage ist an kalten Abenden ein guter Ort, um sich kurz unterzustellen und zu schreiben.
            </p>
          </>
        ),
      },
      {
        title: 'Seen statt Strand',
        body: (
          <p>
            Wo früher Braunkohle abgebaut wurde, liegt heute das Leipziger Neuseenland. Im Sommer radeln die Leute
            zum Cospudener See, liegen am Ufer in der Sonne und greifen zwischen zwei Runden Schwimmen zum Handy. Ein
            Nickname reicht, Name und E-Mail-Adresse bleiben geschützt.
          </p>
        ),
      },
      {
        title: 'Wann Leipzig chattet',
        body: (
          <p>
            Während der Buchmesse im Frühjahr und an den großen Konzertabenden im Gewandhaus ist die Stadt voll, und
            die Gespräche danach dauern bis tief in die Nacht. Im Winter, wenn es früh dunkel wird, sind die Abende
            unter der Decke die liebste Zeit für einen Flirt. Unter der Woche wird es abends lebhaft, wenn die Straßenbahnen aus der Innenstadt rollen und die S-Bahnen durch den City-Tunnel unter dem Markt hindurchfahren. Chatapp läuft im Browser, ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Wie funktioniert der Sexchat in Leipzig?',
        a: <p>Du legst kostenlos ein Profil an, wählst einen Nickname und schickst deine ersten Nachrichten gratis. Danach kosten Nachrichten, Flirts und Fotos Credits, deren Preis du immer vorher siehst.</p>,
      },
      {
        q: 'Muss ich Zahlungsdaten hinterlegen?',
        a: <p>Nein. Für die Anmeldung brauchst du weder Kreditkarte noch Kontodaten. Zahlungsdaten gibst du nur an, wenn du dich entscheidest, Credits zu kaufen.</p>,
      },
      disclosure('Leipzig'),
    ],
    cta: {
      title: 'Bereit für den ersten Flirt?',
      text: 'In einer Minute kostenlos angemeldet, die ersten Nachrichten gratis, von Gohlis bis Connewitz.',
      button: 'Jetzt gratis starten',
    },
  },

  '/dortmund': {
    path: '/dortmund',
    slug: 'dortmund',
    name: 'Dortmund',
    province: 'Nordrhein-Westfalen',
    kicker: 'Größte Stadt des Ruhrgebiets',
    h1: 'Sexchat Dortmund: Flirtchat im Herzen des Ruhrgebiets',
    lead: 'Kostenlos anmelden, die ersten Nachrichten gratis und ein Chat für die Zeit nach dem Abpfiff.',
    intro: (
      <p>
        Dortmund ist mit knapp 600.000 Einwohnern die größte Stadt des Ruhrgebiets. Früher bestimmten Kohle, Stahl
        und Bier den Takt, heute sind es Hochschulen, Technologie und natürlich der Fußball. Die Menschen hier gelten
        als geradeheraus, und so funktioniert auch Chatapp: kostenlos anmelden, die ersten Nachrichten gratis
        schreiben, ohne Umwege.
      </p>
    ),
    sections: [
      {
        title: 'Vom Kreuzviertel zum Phoenix-See',
        body: (
          <>
            <p>
              Das Kreuzviertel mit seinen Altbauten ist das Ausgehviertel der Stadt, mit Kneipen an fast jeder Ecke.
              Im Unionviertel rund um das Dortmunder U, einer ehemaligen Brauerei, die heute ein Zentrum für Kunst
              und Kreativwirtschaft ist, treffen sich Studierende und Kreative. Wo einst das Stahlwerk Phoenix stand,
              liegt heute der Phoenix-See, mit einer Promenade, Restaurants und Booten auf dem Wasser.
            </p>
            <p>
              Im Westfalenpark bietet der Florianturm einen weiten Blick über die Stadt, und auf dem Weg dorthin bleibt
              Zeit für ein paar Zeilen.
            </p>
          </>
        ),
      },
      {
        title: 'Nach dem Spiel',
        body: (
          <p>
            An Spieltagen ist die Stadt schwarz-gelb, und über 80.000 Menschen füllen das größte Stadion
            Deutschlands mit seiner berühmten Südtribüne. Nach dem Abpfiff, egal wie es ausgegangen ist, wird es im
            Chat lebhaft. Auf Chatapp bist du nur ein Nickname: Dein Name und deine E-Mail-Adresse bleiben verborgen,
            und in deinen sozialen Netzwerken taucht nichts auf.
          </p>
        ),
      },
      {
        title: 'Wann Dortmund chattet',
        body: (
          <p>
            Viele Dortmunderinnen und Dortmunder pendeln in die Nachbarstädte, nach Bochum, Essen oder Unna, und
            nutzen die Fahrt im Regionalexpress für ein paar Nachrichten. Abends, wenn die Kneipen im Kreuzviertel
            voll sind, und spät nachts, wenn die Stadt zur Ruhe kommt, ist der Chat am lebendigsten. Und in der Adventszeit, wenn auf dem Hansaplatz der riesige Weihnachtsbaum steht, ziehen sich viele nach dem Glühwein lieber mit dem Handy auf das Sofa zurück. Chatapp läuft
            im Browser, ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Was kostet Chatapp in Dortmund?',
        a: <p>Die Anmeldung ist kostenlos, die ersten Nachrichten ebenfalls. Danach kosten Nachrichten, Flirts und Fotos Credits. Du kaufst nur, wenn du weiterschreiben willst, und kennst den Preis vorher.</p>,
      },
      {
        q: 'Wofür brauche ich Credits?',
        a: <p>Nach deinen Gratisnachrichten bezahlst du mit Credits für jede weitere Nachricht, jeden Flirt und jedes Foto. Wie viele Credits etwas kostet, siehst du auf der Seite, bevor du ein Paket kaufst.</p>,
      },
      disclosure('Dortmund'),
    ],
    cta: {
      title: 'Ohne Umwege zum ersten Flirt',
      text: 'Kostenlos anmelden und die ersten Nachrichten gratis schreiben, ob in Hörde, Aplerbeck oder der Nordstadt.',
      button: 'Kostenlos anmelden',
    },
  },

  '/essen': {
    path: '/essen',
    slug: 'essen',
    name: 'Essen',
    province: 'Nordrhein-Westfalen',
    kicker: 'Grüne Stadt an der Ruhr',
    h1: 'Sexchat Essen: Flirtchat zwischen Zeche und See',
    lead: 'Gratis Profil, kostenlose erste Nachrichten und ein Chat für ruhige Abende und lange Nächte.',
    intro: (
      <p>
        Essen hat sich vom Zentrum der Kohle- und Stahlindustrie zu einer der grünsten Großstädte Deutschlands
        gewandelt: 2017 war die Stadt Grüne Hauptstadt Europas. Rund 580.000 Menschen leben hier zwischen alten
        Zechen, Parks und dem Baldeneysee. Auf Chatapp meldest du dich kostenlos an, schreibst deine ersten
        Nachrichten gratis und gibst dem Abend eine neue Wendung.
      </p>
    ),
    sections: [
      {
        title: 'Von der Rü nach Zollverein',
        body: (
          <>
            <p>
              Die Rüttenscheider Straße ist die beliebteste Ausgehmeile der Stadt, mit Restaurants, Weinbars und
              Cafés, deren Tische im Sommer bis auf den Gehweg reichen. Im Norden steht die Zeche Zollverein, ein
              Weltkulturerbe, in dem heute Museen, Design und Konzerte zu Hause sind. Abends werden die alten
              Fördertürme angestrahlt, ein Anblick, den viele mit einem Foto auf dem Handy festhalten.
            </p>
            <p>
              Im Süden liegt der Grugapark mit seinen Gärten, und rund um das Museum Folkwang ist es an Wochenenden
              lebhaft.
            </p>
          </>
        ),
      },
      {
        title: 'Am Baldeneysee',
        body: (
          <p>
            Der Baldeneysee ist der größte der Ruhrstauseen und im Sommer der Treffpunkt der Stadt, zum Segeln,
            Radfahren oder Liegen im Seaside Beach. Über dem Ufer thront die Villa Hügel, einst das Wohnhaus der
            Familie Krupp. Ob am See oder auf dem Sofa in Werden: Auf Chatapp bist du nur ein Nickname, und dein Name
            und deine E-Mail-Adresse bleiben privat.
          </p>
        ),
      },
      {
        title: 'Wann Essen chattet',
        body: (
          <p>
            Viele arbeiten in den großen Konzernzentralen der Stadt und kommen erst spät nach Hause. Dann ist das
            Sofa der beste Ort für ein Gespräch, das langsam wärmer wird. Am Wochenende pendeln die Leute zwischen
            Essen, Bochum und Düsseldorf hin und her und schreiben in der S-Bahn. Und wenn im Winter die Essener Lichtwochen die Innenstadt beleuchten, ist ein Gespräch am Abend ein schöner Abschluss für einen Bummel über die Kettwiger Straße. Chatapp läuft im Browser, ohne App.
          </p>
        ),
      },
    ],
    faq: [
      {
        q: 'Ist Chatapp in Essen kostenlos?',
        a: <p>Die Anmeldung kostet nichts, und die ersten Nachrichten sind gratis. Danach kosten Nachrichten, Flirts und Fotos Credits. Die Preise siehst du, bevor du etwas kaufst.</p>,
      },
      {
        q: 'Wie diskret ist Chatapp?',
        a: <p>Andere Nutzer sehen nur deinen Nickname, nie deinen Namen oder deine E-Mail-Adresse. Auf sozialen Netzwerken wird nichts veröffentlicht, und welche E-Mails du bekommst, legst du selbst fest.</p>,
      },
      disclosure('Essen'),
    ],
    cta: {
      title: 'Gib dem Abend eine neue Wendung',
      text: 'Kostenlos anmelden und die ersten Nachrichten gratis verschicken, ob in Rüttenscheid, Steele oder Kettwig.',
      button: 'Gratis Profil anlegen',
    },
  },
};
